"use client"
import { useState, useEffect } from "react"
import { FileText, Edit, X } from "lucide-react"
import Dashboard from "../../components/dashboard/Dashboard"
import axios from "axios"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

import { validationPatterns as centralizedPatterns } from '../../utils/validationPatterns';
import { usePageSave } from '../../contexts/PageSaveContext'
import { useServerPageStatus } from '../../contexts/ServerPageStatusContext';

// Configuration - replace with your actual API base URL
import config from "../../config"

export default function TaxReturnForm() {
  const { markPageAsSaved, markPageAsUnsaved } = usePageSave()
  const { refreshAllPageStatuses } = useServerPageStatus()
  const [capacityOpen, setCapacityOpen] = useState(false)
  const [returnSectionOpen, setReturnSectionOpen] = useState(false)
  const [reasonOpen, setReasonOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    capacity: "SELF", // Default to Self capacity to ensure PAN field is disabled initially
    pan: localStorage.getItem("PAN_NO") || "", // Auto-fill PAN from profile
    section: "139(1)-On or before due date",
    filingDate: "",
    receiptNo: "",
    noticeNo: "",
    noticeDateUnderSec: "",
    reason: "",
    checkboxes: {
      deposit: false,
      travel: false,
      electricity: false,
      tds: false,
      savings: false,
    },
    amounts: {
      deposit: "",
      travel: "",
      electricity: "",
      tds: "",
      savings: "",
    },
  })



  // API data state to store the complete API response
  const [apiData, setApiData] = useState({
    fillingId: null,
    formName: "",
    description: "",
    schemaVersion: "",
    formVersion: "",
    returnFilingSection: 0,
    optOutNewTaxRegime: "",
    receiptNo: "",
    noticeNo: "",
    origRetFiledDate: "",
    noticeDateUnderSec: "",
    itrFilingDueDate: "",
    seventhProvisio139: "",
    incrExpAggAmt2LkTrvFrgnCntryFlg: "",
    amtSeventhProvisio139ii: 0,
    incrExpAggAmt1LkElctrctyPrYrFlg: "",
    amtSeventhProvisio139iii: 0,
    clauseiv7provisio139i: "",
    tdSequaltoormorethan25kAmount: 0,
    depositsequaltoormorethan50LAmount: 0,
  })

  // Fetch data from API on component mount
  useEffect(() => {
    fetchFillingData()
  }, [])

  const [returnFillingSectionList, setReturnFillingSectionList] = useState([])
  const [capacityList, setCapacityList] = useState([])
    const getAllCodeList = async (codeType, setDataFn) => {
      try {
        const response = await axios.get(`${config.apiUrl}/system/code/${codeType}`);
        const data = response.data;
  
        if (data && data.codes?.length > 0) {
          console.log(`${codeType} data from API:`, data.codes);
          setDataFn(data.codes);
        } else {
          setDataFn([]); // empty array if no data
        }
      } catch (error) {
        console.log(`Error fetching ${codeType}:`, error);
        toast.error(`Failed to fetch ${codeType}.`);
      }
    };
  
      useEffect(() => {
    getAllCodeList("ReturnFileSec", setReturnFillingSectionList);
    getAllCodeList("Capacity", setCapacityList);
  }, []);

  // Set initial capacity to "Self" when capacityList is loaded
  useEffect(() => {
    console.log("capacityList useEffect triggered, capacityList:", capacityList);
    if (capacityList.length > 0) {
      const selfCapacity = capacityList.find(cap => 
        cap.description?.toLowerCase().includes("self")
      );
      console.log("Found selfCapacity:", selfCapacity);
      if (selfCapacity && formData.capacity !== selfCapacity.codeValue) {
        console.log("Updating formData capacity to:", selfCapacity.codeValue);
        setFormData(prev => ({
          ...prev,
          capacity: selfCapacity.codeValue,
          pan: localStorage.getItem("PAN_NO") || ""
        }));
      }
    }
  }, [capacityList]);

  // Clear validation errors when capacity or PAN changes
  useEffect(() => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.pan;
      return newErrors;
    });
  }, [formData.capacity, formData.pan]);

  const fetchFillingData = async () => {
    try {
      setIsLoading(true)
      const panNo = localStorage.getItem("PAN_NO")
      const year = localStorage.getItem("YEAR")

      if (!panNo || !year) {
        toast.error("PAN number or year not found in localStorage")
        setIsLoading(false)
        return
      }

      const response = await axios.get(`${config.apiUrl}/user/fillingsection/${panNo}/${year}`)

      if (response.data && response.data.fillingSection) {
        const apiResponse = response.data.fillingSection

        // Update API data state
        setApiData(apiResponse)

        // Transform API data to form data structure
        const transformedFormData = {
          capacity: "Self", // This field doesn't seem to be in the API
          pan: panNo,
          section: mapReturnFilingSectionToText(apiResponse.returnFilingSection),
          filingDate: apiResponse.origRetFiledDate || "",
          receiptNo: apiResponse.receiptNo || "",
          noticeNo: apiResponse.noticeNo || "", // This field doesn't seem to be in the API
          noticeDateUnderSec: apiResponse.noticeDateUnderSec || "", // This field doesn't seem to be in the API
          reason: determineReasonFromAPI(apiResponse),
          checkboxes: {
            deposit: apiResponse.depositsequaltoormorethan50LAmount > 0,
            travel: apiResponse.incrExpAggAmt2LkTrvFrgnCntryFlg === "Y",
            electricity: apiResponse.incrExpAggAmt1LkElctrctyPrYrFlg === "Y",
            tds: apiResponse.clauseiv7provisio139i === "Y",
            savings: apiResponse.depositsequaltoormorethan50LAmount > 0,
          },
          amounts: {
            deposit: apiResponse.depositsequaltoormorethan50LAmount?.toString() || "",
            travel: apiResponse.amtSeventhProvisio139ii?.toString() || "",
            electricity: apiResponse.amtSeventhProvisio139iii?.toString() || "",
            tds: apiResponse.tdSequaltoormorethan25kAmount?.toString() || "",
            savings: apiResponse.depositsequaltoormorethan50LAmount?.toString() || "",
          },
        }

        setFormData(transformedFormData)
        toast.success("Data loaded successfully")
      }
    } catch (error) {
      console.error("Error fetching filling data:", error)
      toast.error("Failed to load data")
    } finally {
      setIsLoading(false)
    }
  }

  // Helper function to map return filing section number to text
  const mapReturnFilingSectionToText = (sectionNumber) => {
    switch (sectionNumber) {
      case 11:
        return "139(1)-On or before due date"
      case 12:
        return "139(4)-After due date"
      case 13:
        return "142(1)"
      case 14:
        return "148"
      case 16:
        return "153C"
      case 17:
        return "139(5)-Revised"
      case 18:
        return "139(9)"
      case 20:
        return "119(2)(b)-After condonation of delay"
      default:
        return "139(1)-On or before due date"
    }
  }

  // Helper function to map return filing section text to number
  const mapReturnFilingSectionToNumber = (sectionText) => {
    // Find the matching section from the API data
    const matchingSection = returnFillingSectionList.find(section => {
      const cleanDescription = section.description?.replace(/\s*-\s*/g, '-') || `Section ${section.codeValue}`;
      return cleanDescription === sectionText;
    });
    
    if (matchingSection) {
      return matchingSection.codeValue;
    }
    
    // Fallback to hardcoded mapping
    switch (sectionText) {
      case "139(1)-On or before due date":
        return 11
      case "139(4)-After due date":
        return 12
      case "142(1)":
        return 13
      case "148":
        return 14
      case "153C":
        return 16
      case "139(5)-Revised":
        return 17
      case "139(9)":
        return 18
      case "119(2)(b)-After condonation of delay":
        return 20
      default:
        return 11
    }
  }

  // Helper function to determine reason from API data
  const determineReasonFromAPI = (apiData) => {
    if (apiData.seventhProvisio139 === "Y") {
      return "seventh-proviso"
    }
    // Add more logic here based on your business rules
    return "taxable-income"
  }

  // Comprehensive validation function for all sections
  const validateAllSections = () => {
    const errors = {};

    // 1. Section validation (always required)
    if (!formData.section || formData.section.trim() === "") {
      errors.section = "Section is required";
    }

    // 2. Capacity validation (always required)
    if (!formData.capacity || formData.capacity.trim() === "") {
      errors.capacity = "Capacity is required";
    }

    // 3. PAN validation (always required)
    if (!formData.pan || formData.pan.trim() === "") {
      errors.pan = "PAN is required";
    } else if (!isSelfCapacity() && formData.pan === localStorage.getItem("PAN_NO")) {
      errors.pan = "Representative PAN cannot be same as profile PAN";
    }

    // 4. Reason validation (always required)
    if (!formData.reason || formData.reason.trim() === "") {
      errors.reason = "Reason is required";
    }

    // 5. Conditional field validation based on selected section
    const fieldStates = getFieldStates(formData.section);

    if (fieldStates.filingDate.required && (!formData.filingDate || formData.filingDate.trim() === "")) {
      errors.filingDate = "Original Return Filing Date is required";
    }

    if (fieldStates.receiptNo.required && (!formData.receiptNo || formData.receiptNo.trim() === "")) {
      errors.receiptNo = "Original Return Receipt No. is required";
    }

    if (fieldStates.noticeNo.required && (!formData.noticeNo || formData.noticeNo.trim() === "")) {
      errors.noticeNo = "Notice No. is required";
    }

    if (fieldStates.noticeDate.required && (!formData.noticeDateUnderSec || formData.noticeDateUnderSec.trim() === "")) {
      errors.noticeDate = "Notice Date is required";
    }

    // 6. Amount validation for checked items
    if (formData.checkboxes.deposit && (!formData.amounts.deposit || formData.amounts.deposit.trim() === "")) {
      errors.depositAmount = "Deposit amount is required when deposit is selected";
    }

    if (formData.checkboxes.travel && (!formData.amounts.travel || formData.amounts.travel.trim() === "")) {
      errors.travelAmount = "Travel amount is required when travel is selected";
    }

    if (formData.checkboxes.electricity && (!formData.amounts.electricity || formData.amounts.electricity.trim() === "")) {
      errors.electricityAmount = "Electricity amount is required when electricity is selected";
    }

    if (formData.checkboxes.tds && (!formData.amounts.tds || formData.amounts.tds.trim() === "")) {
      errors.tdsAmount = "TDS amount is required when TDS is selected";
    }

    if (formData.checkboxes.savings && (!formData.amounts.savings || formData.amounts.savings.trim() === "")) {
      errors.savingsAmount = "Savings amount is required when savings is selected";
    }

    return errors;
  };

  // Helper function to check if current capacity is "Self"
  const isSelfCapacity = () => {
    console.log("isSelfCapacity called:");
    console.log("formData.capacity:", formData.capacity);
    console.log("capacityList:", capacityList);
    
    // If capacityList is not loaded yet, check if capacity is "SELF" (our default)
    if (capacityList.length === 0) {
      const result = formData.capacity === "SELF" || formData.capacity === "";
      console.log("capacityList empty, returning:", result);
      return result;
    }
    
    const capacityObj = capacityList.find(cap => cap.codeValue === formData.capacity);
    console.log("Found capacityObj:", capacityObj);
    
    // More comprehensive check for "Self" capacity
    const isSelf = capacityObj?.description?.toLowerCase().includes("self") || 
                   capacityObj?.codeValue?.toLowerCase().includes("self") ||
                   formData.capacity?.toLowerCase().includes("self") ||
                   false;
    
    console.log("Final result:", isSelf);
    return isSelf;
  };

  // Helper function to handle capacity change
  const handleCapacityChange = (newCapacity) => {
    console.log("handleCapacityChange called with:", newCapacity);
    
    // Find the capacity object to get the description
    const capacityObj = capacityList.find(cap => cap.codeValue === newCapacity);
    const capacityDescription = capacityObj?.description || "";
    
    console.log("Found capacityObj:", capacityObj);
    console.log("capacityDescription:", capacityDescription);
    
    // Clear any existing validation errors when capacity changes
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.pan;
      delete newErrors.capacity;
      return newErrors;
    });
    
    // Check if the description contains "Self" (case insensitive)
    if (capacityDescription.toLowerCase().includes("self")) {
      console.log("Setting to Self - auto-filling PAN");
      // Auto-fill PAN from profile when Self is selected
      const profilePAN = localStorage.getItem("PAN_NO");
      setFormData(prev => ({
        ...prev,
        capacity: newCapacity,
        pan: profilePAN || ""
      }));
    } else {
      console.log("Setting to Representative - clearing PAN");
      // Clear PAN when Representative is selected
      setFormData(prev => ({
        ...prev,
        capacity: newCapacity,
        pan: ""
      }));
    }
  };

  // Helper function to determine field states based on selected section
  const getFieldStates = (selectedSection) => {
    const section = selectedSection?.toLowerCase() || "";
    
    if (section.includes("139(1)") || section.includes("139(4)")) {
      // 139(1) or 139(4) - disable all four fields
      return {
        filingDate: { disabled: true, required: false },
        receiptNo: { disabled: true, required: false },
        noticeNo: { disabled: true, required: false },
        noticeDate: { disabled: true, required: false }
      };
    } else if (section.includes("139(5)")) {
      // 139(5) - enable filingDate and receiptNo, make them required
      return {
        filingDate: { disabled: false, required: true },
        receiptNo: { disabled: false, required: true },
        noticeNo: { disabled: false, required: false },
        noticeDate: { disabled: false, required: false }
      };
    } else if (section.includes("139(9)")) {
      // 139(9) - enable all four fields, make them all required
      return {
        filingDate: { disabled: false, required: true },
        receiptNo: { disabled: false, required: true },
        noticeNo: { disabled: false, required: true },
        noticeDate: { disabled: false, required: true }
      };
    } else if (section.includes("142(1)") || section.includes("148") || section.includes("153c")) {
      // 142(1), 148, or 153C - enable noticeNo and noticeDate, make them required
      return {
        filingDate: { disabled: true, required: false },
        receiptNo: { disabled: true, required: false },
        noticeNo: { disabled: false, required: true },
        noticeDate: { disabled: false, required: true }
      };
    } else {
      // Default - enable all fields, make them optional
      return {
        filingDate: { disabled: false, required: false },
        receiptNo: { disabled: false, required: false },
        noticeNo: { disabled: false, required: false },
        noticeDate: { disabled: false, required: false }
      };
    }
  }

  const handleSave = async (modalType) => {
    try {
      setIsSubmitting(true)

      const panNo = localStorage.getItem("PAN_NO")
      const year = localStorage.getItem("YEAR")

      if (!panNo || !year) {
        toast.error("PAN number or year not found")
        return
      }

      // Validate all sections comprehensively
      const errors = validateAllSections();

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        toast.error("Please fill in all required fields in all sections");
        return;
      }

      setValidationErrors({});

      // Transform form data back to API format
      const apiPayload = {
        fillingSection: {
          fillingId: apiData.fillingId,
          formName: apiData.formName,
          description: apiData.description,
          schemaVersion: apiData.schemaVersion,
          formVersion: apiData.formVersion,
          returnFilingSection: mapReturnFilingSectionToNumber(formData.section),
          optOutNewTaxRegime: apiData.optOutNewTaxRegime,
          receiptNo: formData.receiptNo,
          noticeNo: formData.noticeNo,
          origRetFiledDate: formData.filingDate,
          noticeDateUnderSec: formData.noticeDateUnderSec,
          itrFilingDueDate: apiData.itrFilingDueDate,
          seventhProvisio139: formData.reason === "seventh-proviso" ? "Y" : "N",
          incrExpAggAmt2LkTrvFrgnCntryFlg: formData.checkboxes.travel ? "Y" : "N",
          amtSeventhProvisio139ii: Number.parseFloat(formData.amounts.travel) || 0,
          incrExpAggAmt1LkElctrctyPrYrFlg: formData.checkboxes.electricity ? "Y" : "N",
          amtSeventhProvisio139iii: Number.parseFloat(formData.amounts.electricity) || 0,
          clauseiv7provisio139i: formData.checkboxes.tds ? "Y" : "N",
          tdSequaltoormorethan25kAmount: Number.parseFloat(formData.amounts.tds) || 0,
          depositsequaltoormorethan50LAmount: Number.parseFloat(formData.amounts.deposit) || 0,
        },
      }

      const response = await axios.patch(`${config.apiUrl}/user/fillingsection/${panNo}/${year}`, apiPayload)

      if (response.status === 200) {
        const saved = markPageAsSaved('filing-section')
        if (saved) {
          toast.success(`${modalType} details saved successfully`)
        } else {
          toast.error("Please save your profile first before saving filing section details!")
        }

        // Update API data state with the response
        if (response.data && response.data.fillingSection) {
          setApiData(response.data.fillingSection)
        }

        // Close the appropriate modal
        switch (modalType) {
          case "capacity":
            setCapacityOpen(false)
            break
          case "section":
            setReturnSectionOpen(false)
            break
          case "reason":
            setReasonOpen(false)
            break
        }
      }
    } catch (error) {
      console.error(`Error saving ${modalType}:`, error)
      toast.error(`Failed to save ${modalType} details`)
    } finally {
      setIsSubmitting(false)
    }
  }
  
  const handleCheckboxChange = (key, checked) => {
    markPageAsUnsaved('filing-section')
    if (checked == true) {
      setFormData({
        ...formData,
        checkboxes: { ...formData.checkboxes, [key]: checked },
      })
    }
    else {
      setFormData({
        ...formData,
        amounts: { ...formData.amounts, [key]: "" },checkboxes: { ...formData.checkboxes, [key]: checked }
      })
       
    }


  }

  const handleAmountChange = (key, value) => {
     markPageAsUnsaved('filing-section')
     setFormData({
        ...formData,
        amounts: { ...formData.amounts, [key]: value },
      })
  }

  // Add validation function for PAN
  const validateField = (field, value) => {
    if (field === 'pan') {
      const pattern = centralizedPatterns.PAN;
      const stringValue = (value || '').trim();
      let error = '';

      // Only show error if field is filled and length matches pattern
      if (stringValue && stringValue.length === 10 && !pattern.test(stringValue)) {
        error = 'Invalid PAN format. Expected format: ABCDE1234F';
      }
      return error;
    }
    return '';
  };

  // Update the form data change handler to include validation
  const handleFormDataChange = (field, value) => {
    markPageAsUnsaved('filing-section')
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validate field if it's PAN
    const error = validateField(field, value);
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  if (isLoading) {
    return (
      <Dashboard>
        <div className="min-h-screen bg-gray-50 mt-2 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading tax return data...</p>
          </div>
        </div>
      </Dashboard>
    )
  }

  return (
    <Dashboard>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="min-h-screen bg-gray-50 mt-2">
        <div className="max-w-full mx-auto space-y-6">
          {/* Reason for Return Filing Card */}
          <div className="bg-white rounded-lg shadow-sm border border-l-4 border-l-cyan-500 p-6">
            <div className="flex flex-row items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Reason for Return Filing</h3>
                  <p className="text-sm text-gray-600">
                    {formData.reason === "seventh-proviso"
                      ? "Seventh Proviso conditions"
                      : formData.reason === "taxable-income"
                        ? "Taxable Income More than basic Exemption Limit"
                        : "Click here to provide reason"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setReasonOpen(true)}
                className="flex items-center text-cyan-600 hover:text-cyan-700 text-sm font-medium"
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </button>
            </div>
          </div>

          {/* Return Filing Section Card */}
          <div className="bg-white rounded-lg shadow-sm border border-l-4 border-l-cyan-500 p-6">
            <div className="flex flex-row items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Return Filing Section</h3>
                  <p className="text-sm text-gray-600">{formData.section}</p>
                </div>
              </div>
              <button
                onClick={() => setReturnSectionOpen(true)}
                className="flex items-center text-cyan-600 hover:text-cyan-700 text-sm font-medium"
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </button>
            </div>
          </div>

          {/* Capacity Card */}
          <div className="bg-white rounded-lg shadow-sm border border-l-4 border-l-cyan-500 p-6">
            <div className="flex flex-row items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Capacity</h3>
                  <p className="text-sm text-gray-600">{formData.capacity}</p>
                </div>
              </div>
              <button
                onClick={() => setCapacityOpen(true)}
                className="flex items-center text-cyan-600 hover:text-cyan-700 text-sm font-medium"
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </button>
            </div>
          </div>
        </div>

        {/* Reason Modal */}
        {reasonOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl min-h-[90vh] max-h-screen overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-cyan-500 rounded flex items-center justify-center">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Reason for filing of Return <span className="text-red-500">*</span></h2>
                    <p className="text-xs text-gray-500">Update Information</p>
                  </div>
                </div>
                <button onClick={() => setReasonOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 space-y-6 text-xs text-gray-700">
                <div className="space-y-4">
                  {/* Radio - Taxable Income */}
                  <div className="flex items-start sm:items-center space-x-2">
                    <input
                      type="radio"
                      id="taxable-income"
                      name="reason"
                      value="taxable-income"
                      checked={formData.reason === "taxable-income"}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      className="w-5 h-5 text-cyan-600 border-gray-300 focus:ring-cyan-500"
                    />
                    <label htmlFor="taxable-income">Taxable Income More than basic Exemption Limit</label>
                  </div>

                  {/* Radio - Seventh Proviso */}
                  <div className="flex items-start sm:items-center space-x-2">
                    <input
                      type="radio"
                      id="seventh-proviso"
                      name="reason"
                      value="seventh-proviso"
                      checked={formData.reason === "seventh-proviso"}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      className="w-5 h-5 text-cyan-600 border-gray-300 focus:ring-cyan-500"
                    />
                    <label htmlFor="seventh-proviso">
                      Filing return due to conditions under Seventh Proviso to section 139(1):
                    </label>
                  </div>

                  {/* Checkboxes Group */}
                  <div className="space-y-4 pl-4 sm:pl-8">
                    {[
                      {
                        id: "deposit",
                        label: "Deposited more than ₹1 Cr in current/s accounts",
                      },
                      {
                        id: "travel",
                        label: "Incurred over ₹2L for foreign travel",
                      },
                      {
                        id: "electricity",
                        label: "Spent over ₹1L on electricity consumption",
                      },
                      {
                        id: "tds",
                        label: "TDS/TCS aggregate over ₹25k (₹50k for senior citizens)",
                      },
                      {
                        id: "savings",
                        label: "Deposits over ₹50L in one or more savings accounts",
                      },
                    ].map(({ id, label }) => (
                      <div key={id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-start sm:items-center space-x-2">
                          <input
                            type="checkbox"
                            id={id}
                            checked={formData.checkboxes[id] && formData.reason === "seventh-proviso"}
                            onChange={(e) => handleCheckboxChange(id, e.target.checked)}
                            className="w-5 h-5 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                          />
                          <label htmlFor={id}>{label}</label>
                        </div>
                        <div className="flex flex-col">
                          <input
                            type="text"
                            placeholder="Enter Amount"
                            className="w-full sm:w-32 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-xs"
                            value={formData.reason === "seventh-proviso" ? formData.amounts[id] : ""}
                            onChange={(e) => handleAmountChange(id, e.target.value)}
                          />
                          {validationErrors[`${id}Amount`] && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors[`${id}Amount`]}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Radio - Other */}
                  <div className="flex items-start sm:items-center space-x-2">
                    <input
                      type="radio"
                      id="other"
                      name="reason"
                      value="other"
                      checked={formData.reason === "other"}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      className="w-5 h-5 text-cyan-600 border-gray-300 focus:ring-cyan-500"
                    />
                    <label htmlFor="other">Other</label>
                  </div>
                </div>
                {validationErrors.reason && (
                  <div className="text-red-500 text-xs mt-2 p-2 bg-red-50 rounded">
                    {validationErrors.reason}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 p-4 sm:p-6 border-t bg-gray-50">
                <button
                  onClick={() => setReasonOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 text-xs"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSave("reason")}
                  className="px-4 py-2 bg-cyan-500 text-white rounded-md hover:bg-cyan-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-xs disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Details"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Return Section Modal */}
        {returnSectionOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <div className="flex items-center justify-between p-6 border-b">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-cyan-500 rounded flex items-center justify-center">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Return Filing Section</h2>
                    <p className="text-sm text-gray-600">Update Information</p>
                  </div>
                </div>
                <button onClick={() => setReturnSectionOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label htmlFor="section" className="block text-sm font-medium text-gray-700 mb-1">
                    Section <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="section"
                    value={formData.section}
                    onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                  {
                    returnFillingSectionList.map((section) => {
                      // Use the API description directly, but clean it up
                      const cleanDescription = section.description?.replace(/\s*-\s*/g, '-') || `Section ${section.codeValue}`;
                      console.log(`Section: codeValue=${section.codeValue}, original description=${section.description}, clean description=${cleanDescription}`);
                      return (
                        <option key={section.codeValue} value={cleanDescription}>
                          {cleanDescription}
                      </option>
                      );
                    })
                  }
                  </select>
                  {validationErrors.section && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.section}</p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="filing-date" className="block text-sm font-medium text-gray-700 mb-1">
                      Original Return Filing Date
                      {getFieldStates(formData.section).filingDate.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="date"
                      id="filing-date"
                      disabled={getFieldStates(formData.section).filingDate.disabled}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${
                        getFieldStates(formData.section).filingDate.disabled ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      value={formData.filingDate}
                      onChange={(e) => setFormData({ ...formData, filingDate: e.target.value })}
                    />
                    {validationErrors.filingDate && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.filingDate}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="receipt-no" className="block text-sm font-medium text-gray-700 mb-1">
                      Original Return Receipt No.
                      {getFieldStates(formData.section).receiptNo.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="text"
                      id="receipt-no"
                      placeholder="Enter Detail"
                      disabled={getFieldStates(formData.section).receiptNo.disabled}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${
                        getFieldStates(formData.section).receiptNo.disabled ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      value={formData.receiptNo}
                      onChange={(e) => setFormData({ ...formData, receiptNo: e.target.value })}
                    />
                    {validationErrors.receiptNo && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.receiptNo}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="noticeNo" className="block text-sm font-medium text-gray-700 mb-1">
                      Notice No
                      {getFieldStates(formData.section).noticeNo.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="text"
                      id="noticeNo"
                      placeholder="Enter Detail"
                      disabled={getFieldStates(formData.section).noticeNo.disabled}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${
                        getFieldStates(formData.section).noticeNo.disabled ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      value={formData.noticeNo}
                      onChange={(e) => setFormData({ ...formData, noticeNo: e.target.value })}
                    />
                    {validationErrors.noticeNo && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.noticeNo}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="noticeDateUnderSec" className="block text-sm font-medium text-gray-700 mb-1">
                      Notice Date
                      {getFieldStates(formData.section).noticeDate.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="date"
                      id="noticeDateUnderSec"
                      placeholder="Enter Detail"
                      disabled={getFieldStates(formData.section).noticeDate.disabled}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${
                        getFieldStates(formData.section).noticeDate.disabled ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      value={formData.noticeDateUnderSec}
                      onChange={(e) => setFormData({ ...formData, noticeDateUnderSec: e.target.value })}
                    />
                    {validationErrors.noticeDate && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.noticeDate}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
                <button
                  onClick={() => setReturnSectionOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSave("section")}
                  className="px-4 py-2 bg-cyan-500 text-white rounded-md hover:bg-cyan-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Details"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Capacity Modal */}
        {capacityOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <div className="flex items-center justify-between p-6 border-b">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-cyan-500 rounded flex items-center justify-center">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Capacity</h2>
                    <p className="text-sm text-gray-600">Update Information</p>
                  </div>
                </div>
                <button onClick={() => setCapacityOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="return-filing" className="block text-sm font-medium text-gray-700 mb-1">
                      Return Filing by <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="return-filing"
                      value={formData.capacity}
                      onChange={(e) => handleCapacityChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    >
                    {
                      capacityList.map((capacity) => (
                        <option key={capacity.codeValue} value={capacity.codeValue}>
                          {capacity.description}
                        </option>
                      ))
                    }
                    
                    </select>
                    {validationErrors.capacity && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.capacity}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="pan" className="block text-sm font-medium text-gray-700 mb-1">
                      PAN <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="pan"
                      placeholder="Enter PAN"
                      disabled={(() => {
                        const disabled = isSelfCapacity();
                        console.log("PAN field disabled:", disabled);
                        return disabled;
                      })()}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${
                        isSelfCapacity() ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      value={formData.pan}
                      onChange={(e) => setFormData({ ...formData, pan: e.target.value.toUpperCase() })}
                    />
                    {validationErrors.pan && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.pan}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
                <button
                  onClick={() => setCapacityOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSave("capacity")}
                  className="px-4 py-2 bg-cyan-500 text-white rounded-md hover:bg-cyan-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Details"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </Dashboard>
  )
}
